import { Page, View, Text } from '@react-pdf/renderer';
import { styles } from './styles';
import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import { eventTimeFormat } from '@utils/time';
import { getImageSrc } from '@utils/fn';
import { useTranslation } from 'react-i18next';
import { SafeImage } from '../../components/SafeImage';

const BASE_URL = 'https://media.we-conect.com/website/portfolios';

export const PORTFOLIO_TITLE_IMAGES: Record<string, string> = {
  'smart-mobility': `${BASE_URL}/smart-mobility/event_guide_title.jpg`,
  'digital-business-strategy': `${BASE_URL}/digital-business-strategy/event_guide_title.jpg`,
  'it-devops-business': `${BASE_URL}/it-devops-business/event_guide_title.jpg`,
  'big-data-ai': `${BASE_URL}/big-data-ai/event_guide_title.jpg`,
  'smart-manufacturing-industry-4-0': `${BASE_URL}/smart-manufacturing-industry-4-0/event_guide_title.jpg`,
};

export const DEFAULT_TITLE_IMAGE = `${BASE_URL}/smart-mobility/event_guide_title.jpg`;

export const getPortfolioTitleImage = (slug?: string): string => {
  if (!slug) return DEFAULT_TITLE_IMAGE;

  return PORTFOLIO_TITLE_IMAGES[slug] ?? DEFAULT_TITLE_IMAGE;
};

export interface TitlePageProps {
  event: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  footer: JSX.Element;
}

const TitlePage = ({ event, coLocatedEvents, footer }: TitlePageProps) => {
  const { t } = useTranslation();

  const titlePageBigImageUrl = getPortfolioTitleImage(event?.portfolio?.slug);

  return (
    <Page size="A4" orientation="portrait" style={styles.page}>
      <View style={styles.topImageWrapper}>
        <SafeImage
          src={titlePageBigImageUrl}
          style={styles.topImage}
          debugLabel="agenda-title-background"
        />
      </View>

      <View style={styles.centerImageWrapper}>
        <SafeImage
          src={getImageSrc(event?.images?.logo)}
          style={styles.centerImage}
          debugLabel="agenda-title-logo"
        />

        <View style={styles.textContainer}>
          <Text style={styles.title}>{event?.name || ''}</Text>
          <Text style={styles.subtitle}>
            {eventTimeFormat(
              event?.start_date,
              event?.end_date,
              event?.language,
              event?.timezone
            )}{' '}
            | {event?.location?.name}
          </Text>
        </View>
      </View>

      {event?.colocated && (
        <View
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            marginTop: 40,
          }}
        >
          <Text style={styles.coLocatedText}>{t('co-located-with')}</Text>

          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 20,
              width: '100%',
            }}
          >
            {coLocatedEvents
              ?.filter(coLocatedEvent => coLocatedEvent.id !== event.id)
              ?.map((coLocatedEvent, index) => (
                <SafeImage
                  key={index}
                  src={getImageSrc(coLocatedEvent.images.logo)}
                  style={styles.coLocatedImage}
                  debugLabel={`title-page-co-located-logo-${coLocatedEvent?.name}`}
                />
              ))}
          </View>
        </View>
      )}

      {footer}
    </Page>
  );
};

export default TitlePage;
